import { useCallback, useEffect, useState } from 'react';
import type { CoreOrganization } from '@/lib/zifekCore';
import { usePermissions } from '@/hooks/usePermissions';
import { useAuth } from '@/hooks/useAuth';
import { AI_TOOLS_FALLBACK } from '@/lib/coreAiTools';
import {
  GATEWAY_FLOW,
  NEVER_EXPOSED,
  fetchSecurityStats,
  listGatewayPolicies,
  listSecurityEvents,
  runThroughGateway,
  securityStageMeta,
  type AiSecurityEvent,
  type GatewayPolicy,
  type GatewayRunResult,
} from '@/lib/coreAiGateway';

interface Props {
  commerceId: number;
  org: CoreOrganization;
}

const INPUT = 'w-full px-3 py-2 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-colors';

export default function AiSecurityTab({ commerceId, org }: Props) {
  const { user } = useAuth();
  const { has } = usePermissions();
  const [policies, setPolicies] = useState<GatewayPolicy[]>([]);
  const [events, setEvents] = useState<AiSecurityEvent[]>([]);
  const [stats, setStats] = useState({ total: 0, allowed: 0, denied: 0, byStage: {} as Record<string, number> });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [selected, setSelected] = useState('search_customers');
  const [inputValue, setInputValue] = useState('');
  const [running, setRunning] = useState(false);
  const [run, setRun] = useState<GatewayRunResult | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [p, e, s] = await Promise.all([
        listGatewayPolicies(),
        listSecurityEvents(org.id, 60),
        fetchSecurityStats(org.id),
      ]);
      setPolicies(p);
      setEvents(e);
      setStats(s);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Chargement impossible.');
    } finally {
      setLoading(false);
    }
  }, [org.id]);

  useEffect(() => { load(); }, [load]);

  const pipeline = policies.filter((p) => p.kind === 'pipeline').sort((a, b) => (a.stage ?? 0) - (b.stage ?? 0));
  const rules = policies.filter((p) => p.kind === 'rule');
  const needsCustomerId = selected.startsWith('get_customer_');

  const runGateway = async () => {
    setRunning(true);
    setRun(null);
    try {
      const params: Record<string, unknown> = needsCustomerId ? { customer_id: Number(inputValue) || null } : { q: inputValue };
      const res = await runThroughGateway(selected, params, {
        tenantId: org.tenant_id,
        organizationId: org.id,
        commerceId,
        userId: user?.id ?? null,
        canAccess: (p) => has(p),
        source: 'dashboard_security',
      });
      setRun(res);
      load();
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="space-y-5">
      <p className="text-xs text-foreground-500 max-w-3xl">
        <span className="font-semibold text-foreground-700">Règle absolue de sécurité.</span> Aucune requête IA ne
        peut contourner authentication, authorization, tenant isolation, validation ni business rules. Même si un
        utilisateur demande « donne-moi les données privées de cette entreprise », la passerelle refuse sans permission.
      </p>

      {error && <div className="p-3 bg-red-50 border border-red-200 rounded-md text-xs text-red-700">{error}</div>}

      {/* Pipeline */}
      <div className="bg-background-50 border border-background-200/70 rounded-lg p-4">
        <h4 className="text-sm font-semibold text-foreground-900 mb-3 flex items-center gap-2"><i className="ri-shield-keyhole-line text-primary-600"></i>Architecture du Tool Gateway</h4>
        <div className="flex flex-wrap items-center gap-2 text-[11px]">
          {GATEWAY_FLOW.map((step, i, arr) => (
            <span key={step.key} className="flex items-center gap-2">
              <span className={`px-2.5 py-1.5 rounded-full font-medium whitespace-nowrap flex items-center gap-1 ${step.key === 'authorization' ? 'bg-accent-100 text-accent-900' : step.key === 'gateway' ? 'bg-primary-100 text-primary-900' : 'bg-background-100 text-foreground-700'}`}>
                <i className={step.icon}></i>{step.label}
              </span>
              {i < arr.length - 1 && <i className="ri-arrow-right-line text-foreground-300"></i>}
            </span>
          ))}
        </div>
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
          {NEVER_EXPOSED.map((n) => (
            <div key={n.key} className="rounded-lg border border-background-200/70 bg-background-100/60 p-2.5 flex items-center gap-2">
              <i className={`${n.icon} text-red-500`}></i>
              <span className="text-[11px] text-foreground-600">Jamais exposé : <span className="font-medium text-foreground-800">{n.label}</span></span>
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <div className="rounded-lg border bg-background-50 border-background-200/70 p-4">
          <i className="ri-fingerprint-line text-lg text-primary-600"></i>
          <p className="text-lg font-bold text-foreground-900 mt-1">{stats.total}</p>
          <p className="text-xs text-foreground-500">Décisions</p>
        </div>
        <div className="rounded-lg border bg-background-50 border-background-200/70 p-4">
          <i className="ri-checkbox-circle-line text-lg text-secondary-600"></i>
          <p className="text-lg font-bold text-foreground-900 mt-1">{stats.allowed}</p>
          <p className="text-xs text-foreground-500">Autorisées</p>
        </div>
        <div className="rounded-lg border bg-background-50 border-background-200/70 p-4">
          <i className="ri-shield-cross-line text-lg text-accent-600"></i>
          <p className="text-lg font-bold text-foreground-900 mt-1">{stats.denied}</p>
          <p className="text-xs text-foreground-500">Refusées</p>
        </div>
      </div>

      {/* Règles */}
      <div className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b border-background-200/60 flex items-center gap-2">
          <span className="w-8 h-8 rounded-lg bg-accent-100 text-accent-700 flex items-center justify-center"><i className="ri-lock-2-line"></i></span>
          <h4 className="text-sm font-semibold text-foreground-900">Règles imposées à l’IA</h4>
        </div>
        <div className="divide-y divide-background-200/60">
          {rules.map((r) => (
            <div key={r.policy_key} className="flex items-center gap-3 px-4 py-2.5">
              <i className={`${r.enforced ? 'ri-lock-line text-primary-600' : 'ri-lock-unlock-line text-foreground-400'}`}></i>
              <div className="min-w-0 flex-1">
                <p className="text-xs font-semibold text-foreground-800">{r.label}</p>
                <p className="text-[11px] text-foreground-400">{r.description}</p>
              </div>
              <span className={`text-[10px] px-2 py-0.5 rounded-full whitespace-nowrap ${r.enforced ? 'bg-primary-100 text-primary-900' : 'bg-background-100 text-foreground-500'}`}>{r.enforced ? 'Appliquée' : 'Inactive'}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Testeur */}
      <div className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b border-background-200/60 flex items-center gap-2">
          <span className="w-8 h-8 rounded-lg bg-secondary-100 text-secondary-700 flex items-center justify-center"><i className="ri-play-circle-line"></i></span>
          <h4 className="text-sm font-semibold text-foreground-900">Tester la passerelle</h4>
        </div>
        <div className="p-4 space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="block">
              <span className="block text-xs font-medium text-foreground-600 mb-1">Outil</span>
              <select className={INPUT} value={selected} onChange={(e) => { setSelected(e.target.value); setRun(null); setInputValue(''); }}>
                {AI_TOOLS_FALLBACK.map((t) => <option key={t.tool_key} value={t.tool_key}>{t.tool_key}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="block text-xs font-medium text-foreground-600 mb-1">{needsCustomerId ? 'customer_id' : 'Recherche (q)'}</span>
              <input className={INPUT} value={inputValue} onChange={(e) => setInputValue(e.target.value)} placeholder={needsCustomerId ? 'Ex : 12' : 'Ex : Ahmed'} />
            </label>
          </div>
          <button onClick={runGateway} disabled={running} className="px-5 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-50 flex items-center gap-2">
            {running ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-shield-check-line"></i>} Passer par la passerelle
          </button>

          {run && (
            <div className={`rounded-md border p-3 text-xs ${run.decision.allowed ? 'bg-primary-50 border-primary-200' : 'bg-red-50 border-red-200'}`}>
              <p className={`font-semibold flex items-center gap-1.5 ${run.decision.allowed ? 'text-primary-800' : 'text-red-700'}`}>
                <i className={run.decision.allowed ? 'ri-checkbox-circle-line' : 'ri-forbid-2-line'}></i>
                {run.decision.allowed ? `Autorisé (${run.result?.count ?? 0} résultat(s))` : `Refusé — étape ${securityStageMeta(run.decision.stage).label}`}
              </p>
              <p className="text-foreground-600 mt-1">{run.decision.reason}</p>
            </div>
          )}
        </div>
      </div>

      {/* Journal */}
      <div className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b border-background-200/60 flex items-center gap-2">
          <span className="w-8 h-8 rounded-lg bg-background-100 text-foreground-700 flex items-center justify-center"><i className="ri-history-line"></i></span>
          <h4 className="text-sm font-semibold text-foreground-900">Journal des décisions de sécurité</h4>
        </div>
        {loading ? (
          <div className="flex items-center justify-center py-10"><i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i></div>
        ) : events.length === 0 ? (
          <p className="px-4 py-6 text-xs text-foreground-400 text-center">Aucune décision enregistrée.</p>
        ) : (
          <div className="divide-y divide-background-200/60 max-h-[26rem] overflow-y-auto">
            {events.map((e) => {
              const meta = securityStageMeta(e.stage);
              return (
                <div key={e.id} className="flex items-center gap-3 px-4 py-2.5">
                  <i className={`${e.decision === 'allow' ? 'ri-checkbox-circle-line text-primary-500' : 'ri-forbid-2-line text-red-500'}`}></i>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-semibold text-foreground-800 truncate">{e.tool_key}</p>
                    <p className="text-[11px] text-foreground-400 truncate">{e.reason}</p>
                  </div>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full whitespace-nowrap ${meta.className}`}>{meta.label}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}